/**
 * HustleUpPublicGroupPage — /up/g/:slug
 *
 * Unauthenticated, SEO-optimised landing page for a single HustleUp
 * group.  Injects JSON-LD (Organization + Event schema) into <head>
 * for rich Google search results, plus Open Graph + Twitter Cards
 * so link previews look polished.
 *
 * Anyone with the URL sees the group's name, region, description,
 * host first-names, member count, upcoming + past events, and a
 * "Join this group" CTA that bounces to the auth wall if signed-out.
 *
 * Sister pages:
 *   /up/groups          → HustleUpDiscoverPage
 *   /up/e/:event_id     → HustleUpPublicEventPage (same file, exported)
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Calendar, MapPin, ArrowLeft, ArrowRight, Globe,
         Hash, Loader2, Share2, CalendarPlus, Sparkles, Lock } from "lucide-react";
import "./HustleUpPublicGroupPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

/* ── <head> injection helpers ──────────────────────────────────── */
function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function injectJsonLd(id, payload) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id   = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

function clearJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

function formatDate(iso) {
  if (!iso) return "TBA";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    }) + " · " + d.toLocaleTimeString("en-AU", {
      hour: "numeric", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

/* ─────────────────────────────────────────────────────────────────
 * GROUP LANDING
 * ──────────────────────────────────────────────────────────────── */
export default function HustleUpPublicGroupPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Sprint 4 — Attached Circles
  const [circles,      setCircles]     = useState([]);
  const [circlesLoaded, setCirclesLoaded] = useState(false);
  const [eligibility,  setEligibility]  = useState({});

  useEffect(() => {
    let dead = false;
    setLoading(true);
    Promise.allSettled([
      api.get(`/api/hustleup/public/groups/${slug}`),
      api.get(`/api/hustleup/public/groups/${slug}/seo`),
    ]).then(([gr, seo]) => {
      if (dead) return;
      if (gr.status !== "fulfilled") {
        setError(gr.reason?.response?.status === 404
          ? "Group not found." : "Could not load group.");
        setLoading(false);
        return;
      }
      const g = gr.value.data;
      setGroup(g);
      // ── Fetch attached Circles for this Meet group ─────────
      const mgKey = g.id || g.slug;
      if (mgKey) {
        api.get(`/api/hustlemeet-circles/by-meet-group/${encodeURIComponent(mgKey)}`)
          .then((res) => {
            if (dead) return;
            const list = res?.data?.circles || [];
            setCircles(list);
            setCirclesLoaded(true);
            // If the user is signed in, fetch eligibility for each Circle
            const token = localStorage.getItem("hu_session_token")
                       || localStorage.getItem("token")
                       || localStorage.getItem("access_token");
            if (token && list.length) {
              const H = { headers: { Authorization: `Bearer ${token}` } };
              Promise.allSettled(list.map((c) =>
                api.get(`/api/hustlemeet-circles/${c.id}/eligibility`, H)
                   .then((r) => [c.id, r.data])
              )).then((results) => {
                if (dead) return;
                const map = {};
                for (const r of results) {
                  if (r.status === "fulfilled") {
                    const [cid, data] = r.value;
                    map[cid] = data;
                  }
                }
                setEligibility(map);
              });
            }
          })
          .catch(() => { if (!dead) setCirclesLoaded(true); });
      }
      // Set page meta
      document.title = `${g.name} · HustleUp${g.region ? ` · ${g.region}` : ""}`;
      const desc = (g.description || "").slice(0, 160);
      setMeta("description", desc);
      setMeta("og:title",       `${g.name} on HustleUp`, true);
      setMeta("og:description", desc, true);
      setMeta("og:type",        "website", true);
      setMeta("og:url",         g.public_url, true);
      if (g.image_url) setMeta("og:image", g.image_url, true);
      setMeta("twitter:card",   "summary_large_image");
      setMeta("twitter:title",  `${g.name} on HustleUp`);
      setMeta("twitter:description", desc);
      if (g.image_url) setMeta("twitter:image", g.image_url);
      // JSON-LD
      if (seo.status === "fulfilled") {
        injectJsonLd("hu-jsonld-group", seo.value.data);
      }
      setLoading(false);
    });
    return () => {
      dead = true;
      clearJsonLd("hu-jsonld-group");
    };
  }, [slug]);

  const share = async () => {
    const url = group?.public_url || window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: group?.name, text: group?.description?.slice(0, 80), url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch (_) { /* user cancelled */ }
  };

  const onJoin = () => {
    const token = localStorage.getItem("hu_session_token");
    if (token) {
      navigate(`/hustleup/groups/${slug}`);
    } else {
      navigate(`/hustleup/join?return=${encodeURIComponent(`/up/g/${slug}`)}`);
    }
  };

  if (loading) {
    return (
      <div className="hupg-root">
        <div className="hupg-loading"><Loader2 className="hupg-spin" /> Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="hupg-root">
        <header className="hupg-topbar">
          <Link to="/up/groups" className="hupg-back">
            <ArrowLeft size={14} /> All groups
          </Link>
        </header>
        <div className="hupg-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="hupg-root" data-testid="hu-public-group-root">
      {/* Topbar */}
      <header className="hupg-topbar">
        <Link to="/up/groups" className="hupg-back" data-testid="hu-public-back">
          <ArrowLeft size={14} /> All groups
        </Link>
        <div className="hupg-topbar__right">
          <button onClick={share} className="hupg-share" data-testid="hu-public-share">
            <Share2 size={14} /> Share
          </button>
          <button onClick={onJoin} className="hupg-cta" data-testid="hu-public-join">
            Join group <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section
        className="hupg-hero"
        style={group.image_url ? { backgroundImage:
          `linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.78) 100%), url(${group.image_url})` }
          : undefined}
      >
        <div className="hupg-hero__inner">
          <span className="hupg-hero__cat">
            <Hash size={11} /> {group.category}
          </span>
          <h1 className="hupg-hero__title">{group.name}</h1>
          <div className="hupg-hero__meta">
            <span><MapPin size={12} /> {group.region}</span>
            <span><Users size={12} /> {group.member_count} {group.member_count === 1 ? "member" : "members"}</span>
            <span><Calendar size={12} /> {group.upcoming_events?.length || 0} upcoming</span>
          </div>
        </div>
      </section>

      <main className="hupg-body">
        {/* About */}
        <section className="hupg-section">
          <h2 className="hupg-section__h">About this group</h2>
          <p className="hupg-about" data-testid="hu-public-description">
            {group.description}
          </p>
          {group.topics?.length > 0 && (
            <div className="hupg-tags">
              {group.topics.slice(0, 14).map((t) => (
                <span key={t} className="hupg-tag">{t}</span>
              ))}
            </div>
          )}
        </section>

        {/* Hosts */}
        {group.hosts?.length > 0 && (
          <section className="hupg-section">
            <h2 className="hupg-section__h">Organisers</h2>
            <div className="hupg-hosts" data-testid="hu-public-hosts">
              {group.hosts.map((h, i) => (
                <div className="hupg-host" key={i}>
                  <div className="hupg-host__avatar">
                    {(h.first_name || "?").charAt(0)}
                  </div>
                  <div>
                    <div className="hupg-host__name">
                      {h.first_name} {h.is_lead && <span>(lead)</span>}
                    </div>
                    {h.city && <div className="hupg-host__city">{h.city}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming events */}
        <section className="hupg-section">
          <h2 className="hupg-section__h">Upcoming events</h2>
          {(group.upcoming_events || []).length === 0 ? (
            <p className="hupg-empty">No events scheduled. Hosts post new events regularly — join to get notified.</p>
          ) : (
            <div className="hupg-events">
              {group.upcoming_events.map((e) => (
                <Link
                  key={e.event_id}
                  to={`/up/e/${e.event_id}`}
                  className="hupg-event"
                  data-testid="hu-public-event-card"
                >
                  <div className="hupg-event__date">
                    {formatDate(e.starts_at)}
                  </div>
                  <h3 className="hupg-event__title">{e.title}</h3>
                  <div className="hupg-event__meta">
                    {e.is_online
                      ? <><Globe size={11}/> Online</>
                      : <><MapPin size={11}/> {e.venue_name || e.venue_addr || "TBA"}</>}
                    <span className="hupg-event__rsvp">
                      {e.rsvp_yes} going{e.capacity ? ` / ${e.capacity}` : ""}
                    </span>
                  </div>
                  {e.description_preview && (
                    <p className="hupg-event__preview">{e.description_preview}…</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* ── Sprint 4 · Inner Circle panel (attached Circles) ── */}
        {circlesLoaded && circles.length > 0 && (
          <section
            className="hupg-section"
            data-testid="hu-inner-circle-panel"
            style={{ borderTop: "1px dashed #d97706", paddingTop: 24 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Sparkles size={14} color="#f59e0b" />
              <span style={{ textTransform: "uppercase", letterSpacing: "0.28em",
                             fontSize: 11, fontWeight: 900, color: "#d97706" }}>
                Inner Circle{circles.length > 1 ? "s" : ""}
              </span>
            </div>
            <h2 className="hupg-section__h" style={{ marginTop: 0 }}>
              Get closer with the regulars
            </h2>
            <p className="hupg-about" style={{ marginBottom: 18 }}>
              This group has {circles.length} attached Circle{circles.length > 1 ? "s" : ""} — smaller, curated dinners at partnered venues. Show up to a few Meet events, build your reliability score, and unlock an invitation to apply.
            </p>
            <div style={{ display: "grid", gap: 12 }}>
              {circles.map((c) => {
                const el = eligibility[c.id] || {};
                const rules   = el.rules   || c.unlock_rules || {};
                const prog    = el.progress || null;
                const eligible = el.eligible;
                const rsvpsNeed = rules.min_rsvps    ?? 3;
                const attNeed   = rules.min_turn_ups ?? 2;
                const openOut   = rules.open_to_outside;
                const rsvpsHave = prog?.rsvps    ?? 0;
                const attHave   = prog?.turn_ups ?? 0;
                const pct = Math.min(100, Math.round(
                  ((rsvpsHave + attHave) /
                   Math.max(1, rsvpsNeed + attNeed)) * 100));
                return (
                  <div
                    key={c.id}
                    data-testid={`hu-inner-circle-card-${c.id}`}
                    style={{ padding: 16, borderRadius: 14,
                             border: "1px solid #e7e5e4", background: "#fff" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "flex-start", gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h3 style={{ fontWeight: 900, fontSize: 15, margin: 0,
                                     color: "#0c0a09" }}>
                          {c.name}
                        </h3>
                        {c.tagline && (
                          <p style={{ margin: "4px 0 0", fontSize: 12,
                                       color: "#78716c" }}>{c.tagline}</p>
                        )}
                        <div style={{ marginTop: 8, display: "flex",
                                       flexWrap: "wrap", gap: 10, fontSize: 11,
                                       color: "#78716c" }}>
                          <span>Cadence: <strong>{c.cadence}</strong></span>
                          {c.default_party_size && (
                            <span>Table: <strong>{c.default_party_size}</strong></span>
                          )}
                          {openOut && (
                            <span style={{ color: "#d97706", fontWeight: 700 }}>
                              Open to outside applications
                            </span>
                          )}
                        </div>
                      </div>
                      {eligible === true ? (
                        <button
                          type="button"
                          onClick={() => navigate(
                            `/circles/${c.slug || c.id}`)}
                          data-testid={`hu-inner-circle-apply-${c.id}`}
                          style={{ padding: "8px 14px", borderRadius: 9999,
                                   background: "#d97706", color: "#fff",
                                   fontWeight: 900, fontSize: 12, border: "none",
                                   whiteSpace: "nowrap", cursor: "pointer" }}
                        >
                          Apply to Circle <ArrowRight size={12}
                            style={{ display: "inline", marginLeft: 4,
                                     verticalAlign: "middle" }}/>
                        </button>
                      ) : eligible === false ? (
                        <span
                          data-testid={`hu-inner-circle-locked-${c.id}`}
                          title="Show up to more Meet events to unlock"
                          style={{ padding: "8px 14px", borderRadius: 9999,
                                   background: "#f5f5f4", color: "#78716c",
                                   fontWeight: 900, fontSize: 12,
                                   whiteSpace: "nowrap" }}
                        >
                          <Lock size={11} style={{ display: "inline",
                            marginRight: 4, verticalAlign: "middle" }}/>
                          Keep going
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => navigate(
                            `/hustleup/join?return=/up/g/${slug}`)}
                          data-testid={`hu-inner-circle-signin-${c.id}`}
                          style={{ padding: "8px 14px", borderRadius: 9999,
                                   background: "#0c0a09", color: "#fef3c7",
                                   fontWeight: 900, fontSize: 12, border: "none",
                                   whiteSpace: "nowrap", cursor: "pointer" }}
                        >
                          Sign in to check
                        </button>
                      )}
                    </div>

                    {eligible === false && prog && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between",
                                       fontSize: 11, color: "#78716c",
                                       marginBottom: 4 }}>
                          <span>RSVPs {rsvpsHave} / {rsvpsNeed}</span>
                          <span>Turn-ups {attHave} / {attNeed}</span>
                        </div>
                        <div style={{ width: "100%", height: 6, background: "#f5f5f4",
                                       borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%",
                                         background: "linear-gradient(90deg, #f59e0b, #d97706)" }}/>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Past events (social proof) */}
        {group.past_events?.length > 0 && (
          <section className="hupg-section">
            <h2 className="hupg-section__h">Past events</h2>
            <ul className="hupg-past">
              {group.past_events.map((e) => (
                <li key={e.event_id}>
                  <span className="hupg-past__date">{formatDate(e.starts_at)}</span>
                  <span className="hupg-past__title">{e.title}</span>
                  <span className="hupg-past__rsvp">{e.rsvp_yes} attended</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Tail CTA */}
        <section className="hupg-tail">
          <h3>Want to join {group.name}?</h3>
          <p>Free to join. Free events. No subscription fees.</p>
          <button onClick={onJoin} className="hupg-tail__cta" data-testid="hu-public-tail-join">
            Join group <ArrowRight size={14} />
          </button>
        </section>
      </main>

      <footer className="hupg-footer">
        Powered by Hustle<span style={{ color: "#fbbf24" }}>Up</span>.au
        · Free to host. Free to attend.
      </footer>
    </div>
  );
}
